import { Injectable } from '@angular/core';
import { IPreloadedImageResult } from 'src/app/interfaces/ipreloaded-image-result';
import { CallStoreService } from 'src/app/pages/learner/call/library/call-store/call-store.service';
import { LoggerService } from '../../logger/logger.service';

@Injectable({
  providedIn: 'root',
})
export class ImagePreloaderService {
  private readonly imageExtensions: string[] = [
    '.jpeg',
    '.jpg',
    '.png',
    '.gif',
  ];
  private loadedImageIndices: Set<number> = new Set();
  private loadingPromises: Map<number, Promise<IPreloadedImageResult>> =
    new Map();
  private preloadedImages: HTMLImageElement[] = [];
  private imgArray: string[] = [];

  constructor(
    private callStore: CallStoreService,
    private logger: LoggerService
  ) { }

  /**
   * Preloads multiple images with extension fallback
   * @param baseUrl Single base URL (without extensions)
   * @param imageIndexes Array of image indexes to load
   * @returns Promise that resolves when all images are processed
   */
  preloadImages(
    baseUrl: string,
    imageIndexes: number[]
  ): Promise<IPreloadedImageResult[]> {
    // Create an array of promises for each image index
    const promises = imageIndexes.map((index) =>
      this.loadImageWithExtensions(`${baseUrl}${index}`, index)
    );

    // Use Promise.allSettled to handle all promises and filter out the fulfilled ones
    return Promise.allSettled(promises).then((results) => {
      return results
        .filter(
          (result): result is PromiseFulfilledResult<IPreloadedImageResult> =>
            result.status === 'fulfilled'
        )
        .map((result) => result.value);
    });
  }

  /**
   * Loads a single image trying different extensions
   * @param baseSrc Base source URL without extension
   * @param index Image index
   * @returns Promise that resolves with image data or rejects if all extensions fail
   */
  loadImageWithExtensions(
    baseSrc: string,
    index: number
  ): Promise<IPreloadedImageResult> {
    // Return existing promise if already loading
    if (this.loadingPromises.has(index)) {
      // console.log(`Image ${index} is already being loaded, returning existing promise`);
      return this.loadingPromises.get(index)!;
    }

    // Return cached result if already loaded
    if (this.loadedImageIndices.has(index)) {
      console.log(`Image ${index} already loaded, skipping`);
      return Promise.reject(new Error(`Image ${index} already loaded`));
    }

    const loadPromise = new Promise<IPreloadedImageResult>(
      (resolve, reject) => {
        let attempts = 0;
        let isResolved = false; // Flag to prevent multiple resolutions

        const tryNextExtension = (): void => {
          if (isResolved) return; // Stop if already resolved

          if (attempts < this.imageExtensions.length) {
            const extension = this.imageExtensions[attempts];
            const src = baseSrc + extension;
            attempts++;

            // console.log(`Trying to load Image ${index} with extension ${extension}`);

            this.loadImage(src, index)
              .then((result) => {
                if (!isResolved) {
                  isResolved = true;
                  console.log(
                    `- Successfully loaded Image ${index} with extension ${extension}`
                  );
                  resolve(result);
                }
              })
              .catch(() => {
                if (!isResolved) {
                  console.log(
                    `- Failed to load Image ${index} with extension ${extension}`
                  );
                  tryNextExtension();
                }
              });
          } else {
            if (!isResolved) {
              isResolved = true;
              const error = `Failed to load image: ${baseSrc} with any extension`;
              console.warn(error);
              reject(new Error(error));
            }
          }
        };

        tryNextExtension();
      }
    );

    // Store the promise to prevent duplicate loading
    this.loadingPromises.set(index, loadPromise);

    // Clean up the promise when done (success or failure)
    loadPromise.finally(() => {
      this.loadingPromises.delete(index);
    });

    return loadPromise;
  }

  /**
   * Loads a single image
   * @param src Complete image source URL
   * @param index Image index
   * @returns Promise that resolves with image data or rejects on error
   */
  loadImage(src: string, index: number): Promise<IPreloadedImageResult> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        // Double-check to prevent race conditions
        if (!this.loadedImageIndices.has(index)) {
          this.imgArray.push(src);
          this.preloadedImages.push(img);
          this.loadedImageIndices.add(index);

          const extensionMatch = src.match(/\.(jpeg|jpg|png|gif)$/i);
          const extension = extensionMatch ? extensionMatch[1] : 'jpeg';

          this.imgArray = this.sortImagesByNumber(this.imgArray, extension);
          this.logger.log('[CALL] visual aid - loaded images', {
            src,
            imageIndex: index
          });
          resolve({ src, image: img, index });
        } else {
          reject(new Error(`Image already loaded: ${src}`));
        }
      };

      img.onerror = () => {
        this.logger.log('[CALL] visual aid - failed preload images', {
          src,
          imageIndex: index
        });
        reject(new Error(`Failed to load image: ${src}`));
      };

      img.src = src;
    });
  }

  /**
   * Sorts image array by numeric value in filename
   * @param imgArray Array of image URLs
   * @param extension File extension to match
   * @returns Sorted array
   */
  sortImagesByNumber(imgArray: string[], extension: string): string[] {
    return imgArray.sort((a, b) => {
      const extractNumber = (url: string): number => {
        const regex = new RegExp(`(\\d+)(?=\\.${extension})`, 'i');
        const match = url.match(regex);
        return match ? parseInt(match[0], 10) : 0;
      };

      const numA = extractNumber(a);
      const numB = extractNumber(b);
      return numA - numB;
    });
  }

  /**
   * Gets all loaded image URLs
   * @returns Array of image URLs
   */
  getImageArray(): string[] {
    return [...this.imgArray];
  }

  /**
   * Clears all loaded images and resets state
   */
  clearCache(): void {
    this.loadedImageIndices.clear();
    this.loadingPromises.clear();
    this.preloadedImages = [];
    this.imgArray = [];
    this.callStore.setVisualAidImages([]);
    this.callStore.setVisualAidImageUrl('');
    console.log('Image preloader cache cleared');
  }

  /**
   * Checks if an image with given index is already loaded
   * @param index Image index to check
   * @returns True if image is loaded
   */
  isImageLoaded(index: number): boolean {
    return this.loadedImageIndices.has(index);
  }

  /**
   * Checks if an image is currently being loaded
   * @param index Image index to check
   * @returns True if image is being loaded
   */
  isImageLoading(index: number): boolean {
    return this.loadingPromises.has(index);
  }

  /**
   * Extracts unique visual aid indices from an array of items
   * @param items Array of objects that may contain visualAidIndex property
   * @returns Array of unique visual aid indices
   */
  getVisualAidIndices(items: any[]): number[] {
    const indexSet = new Set<number>();

    items.forEach((item) => {
      if (item?.visualAidIndex !== undefined) {
        indexSet.add(item.visualAidIndex);
      }
    });

    return Array.from(indexSet);
  }

  getImageSrcByIndex(index: number): string | null {
    const images = this.callStore.getVisualAidImages();
    if (images) {
      const found = images.find((img) => img.index === index);
      return found ? found.src : null;
    }
    return null;
  }
}
